import type { EmberLobbyOffer } from '../net/ember-api';
import { currencyIcon } from './currency';
import { iconMarkup } from './icon';
import { esc } from './item-icon';
import { formatEur } from './shop';
import { t } from '../i18n/i18n';

/**
 * The limited-time Ember offer, greeting a player as they enter the Sconce.
 *
 * User (2026-10-09): "when the player enters the game lobby, a special offer
 * for purchasing premium currency should pop up" — once per day, timed bonus.
 * What the bonus is and why it is bounded lives in the backend's
 * `ember-offer.ts`; this module only draws it and keeps the daily rhythm.
 *
 * **Once a day, per device.** Remembered in localStorage, not on the server:
 * it is a courtesy against nagging, not a rule anything depends on, and a
 * player seeing it once more on a second device is harmless. Storage that
 * throws (private mode) reads as "not shown yet" — the popup then appears on
 * each visit that day, which is the louder failure but never a broken hub.
 */

const SEEN_KEY = 'tallow:ember-offer:seen';

/** Local calendar day, so "once a day" turns over at the player's midnight. */
export function localDay(now: Date): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

export function shouldShowOffer(userId: string, now: Date, storage: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return storage?.getItem(`${SEEN_KEY}:${userId}`) !== localDay(now);
  } catch {
    return true;
  }
}

/** Undo today's mark — used when the player is sent off to fix something first. */
export function forgetOfferShown(userId: string, storage: Pick<Storage, 'removeItem'> | null): void {
  try { storage?.removeItem(`${SEEN_KEY}:${userId}`); } catch { /* see header */ }
}

export function markOfferShown(userId: string, now: Date, storage: Pick<Storage, 'setItem'> | null): void {
  try { storage?.setItem(`${SEEN_KEY}:${userId}`, localDay(now)); } catch { /* see header */ }
}

/**
 * `2d 4h` → `3h 12m` → `4m 09s`. Two units at most: a countdown that shows
 * seconds for a week is a countdown nobody reads, and one that hides them in
 * the last minutes undersells the only moment urgency is true.
 */
export function countdownText(msLeft: number): string {
  if (msLeft <= 0) return t('Ended');
  const s = Math.floor(msLeft / 1000);
  const days = Math.floor(s / 86_400);
  const hours = Math.floor((s % 86_400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return t('{d}d {h}h', { d: days, h: hours });
  if (hours > 0) return t('{h}h {m}m', { h: hours, m: String(mins).padStart(2, '0') });
  return t('{m}m {s}s', { m: mins, s: String(secs).padStart(2, '0') });
}

export function emberOfferMarkup(offer: EmberLobbyOffer, now: Date): string {
  const left = new Date(offer.endsAt).getTime() - now.getTime();
  return `
    <div data-offer-card tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="ember-offer-title"
         class="frame-raised relative w-full sm:max-w-[420px] overflow-hidden outline-none
                rounded-b-none sm:rounded-b-[var(--radius-lg)] pb-[env(safe-area-inset-bottom)]">
      <div aria-hidden="true" class="absolute inset-0 pointer-events-none
           bg-[radial-gradient(60%_45%_at_50%_22%,rgba(176,124,255,0.28),transparent_70%)]"></div>

      <button data-offer-close type="button" aria-label="${esc(t('Close offer'))}"
        class="btn btn-quiet absolute top-5 right-5 z-10 min-w-11 px-0 justify-center">${iconMarkup('close')}</button>

      <div class="relative flex flex-col items-center text-center gap-3 px-5 pt-7 pb-5 sm:px-7">
        <p class="eyebrow">${esc(t('Limited offer'))}</p>
        <h2 id="ember-offer-title" class="font-heading text-gold-bright text-2xl tracking-wide leading-tight">
          ${esc(t('Kindle your Ember'))}
        </h2>

        <div class="relative my-1">
          <span class="block animate-flicker drop-shadow-[0_0_24px_rgba(176,124,255,0.55)]">${currencyIcon('ember', 5.5)}</span>
          <span class="animate-seal absolute -top-1 -right-7 rounded-full border border-[#8e6bd0]
                       bg-[linear-gradient(180deg,#7d58c4,#4b2f86)] text-white font-heading font-bold
                       text-[13px] leading-none px-2.5 py-2 whitespace-nowrap">+${offer.bonusPct}%</span>
        </div>

        <div class="flex flex-col items-center gap-1">
          <p class="font-heading text-[2rem] leading-none text-purple-light tabular-nums">
            <span data-offer-total>${offer.totalEmber}</span> <span class="text-base text-text-dim">${esc(t('Ember'))}</span>
          </p>
          <p class="text-[13px] text-text-dim tabular-nums">
            ${t('{base} + {bonus} for {price}', {
              base: `<span class="line-through decoration-error/70">${offer.baseEmber}</span>`,
              bonus: `<span class="text-purple-light font-semibold">${esc(t('{n} bonus', { n: offer.bonusEmber }))}</span>`,
              price: esc(formatEur(offer.eurCents)),
            })}
          </p>
        </div>

        <p class="chip text-[12px]" aria-live="off">
          ${iconMarkup('clock', 14)} ${t('Ends in {time}', { time: `<span data-offer-countdown class="tabular-nums">${countdownText(left)}</span>` })}
        </p>

        <button data-offer-claim type="button" class="btn btn-gold sheen w-full min-h-12 text-[14px] mt-1">
          ${esc(t('Claim for {price}', { price: formatEur(offer.eurCents) }))}
        </button>
        <p data-offer-error class="text-[13px] text-error" role="alert" hidden></p>
        <button data-offer-later type="button" class="btn btn-quiet w-full">${esc(t('Not now'))}</button>

        <p class="text-[11px] text-text-dim leading-snug">
          ${esc(t('One per player. The bonus is added when the payment clears. Ember still exchanges into Wax at the usual daily limit.'))}
        </p>
      </div>
    </div>`;
}

export interface EmberOfferPopupOptions {
  /**
   * Start the checkout. Resolves with an error sentence to show in place, or
   * null once the page is navigating away to the provider.
   */
  onClaim: (offer: EmberLobbyOffer) => Promise<string | null>;
}

export interface EmberOfferPopup {
  close(): void;
}

/**
 * Mounts over `container`. Escape, the backdrop, the close mark and "Not
 * now" all dismiss; the countdown is recomputed from `endsAt` each second
 * (lazy, nothing ticks server-side) and the offer closes itself at zero.
 */
export function openEmberOfferPopup(
  container: HTMLElement,
  offer: EmberLobbyOffer,
  opts: EmberOfferPopupOptions,
): EmberOfferPopup {
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[60] scrim flex items-end sm:items-center justify-center sm:p-6';
  overlay.innerHTML = emberOfferMarkup(offer, new Date());
  container.appendChild(overlay);

  const card = overlay.querySelector<HTMLElement>('[data-offer-card]')!;
  const countdown = overlay.querySelector<HTMLElement>('[data-offer-countdown]')!;
  const claim = overlay.querySelector<HTMLButtonElement>('[data-offer-claim]')!;
  const errorEl = overlay.querySelector<HTMLElement>('[data-offer-error]')!;
  const previousFocus = document.activeElement as HTMLElement | null;

  let closed = false;
  const endsAt = new Date(offer.endsAt).getTime();
  const timer = setInterval(() => {
    const left = endsAt - Date.now();
    countdown.textContent = countdownText(left);
    if (left <= 0) close();
  }, 1000);

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') { e.stopPropagation(); close(); }
  }

  function close(): void {
    if (closed) return;
    closed = true;
    clearInterval(timer);
    document.removeEventListener('keydown', onKey, true);
    // Leaves the way it came, unless motion is off — then it simply goes.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { overlay.remove(); } else {
      overlay.style.transition = 'opacity 160ms ease';
      card.style.transition = 'transform 160ms ease';
      overlay.style.opacity = '0';
      card.style.transform = 'translateY(14px) scale(0.98)';
      setTimeout(() => overlay.remove(), 170);
    }
    previousFocus?.focus?.();
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-offer-close]')!.addEventListener('click', close);
  overlay.querySelector('[data-offer-later]')!.addEventListener('click', close);
  // Capture phase, so the hub's own Escape handler does not also close a panel underneath.
  document.addEventListener('keydown', onKey, true);

  claim.addEventListener('click', async () => {
    claim.disabled = true;
    errorEl.hidden = true;
    const error = await opts.onClaim(offer).catch((err: unknown) => (err instanceof Error ? err.message : t('Something went wrong.')));
    if (error) {
      errorEl.textContent = error;
      errorEl.hidden = false;
      claim.disabled = false;
    }
  });

  card.focus();
  return { close };
}
